import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { UserPlus, Edit2, Trash2, ChevronLeft, Save, X, AlarmClock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

type UserWithRole = {
  id: string;
  email: string;
  created_at: string;
  role: string | null;
  role_id: string | null;
  nom: string | null;
  prenom: string | null;
  address_mail: string | null;
};

const AdminUsers = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<UserWithRole | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('etudiant');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get all user roles from the user_roles table with the new fields
      const { data: userRolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('id, user_id, role, created_at, nom, prenom, address_mail');

      if (rolesError) {
        throw rolesError;
      }

      // Create a map of user IDs to emails from user_roles table directly
      const emailMap = new Map();
      userRolesData.forEach(role => {
        if (role.address_mail) {
          emailMap.set(role.user_id, role.address_mail);
        }
      });

      // Transform the data to match what we need
      const userWithRoles = userRolesData.map(role => ({
        id: role.user_id,
        email: role.address_mail || emailMap.get(role.user_id) || 'Email non disponible',
        created_at: role.created_at,
        role: role.role,
        role_id: role.id,
        nom: role.nom,
        prenom: role.prenom,
        address_mail: role.address_mail
      }));

      // As a fallback, also try to get emails from the students table
      // for any users whose email we couldn't find
      const usersNeedingEmails = userWithRoles.filter(u => u.email === 'Email non disponible');
      
      if (usersNeedingEmails.length > 0) {
        const userIds = usersNeedingEmails.map(u => u.id);
        
        // Try to find emails in students table
        const { data: studentData } = await supabase
          .from('students')
          .select('id, email, first_name, last_name')
          .in('id', userIds);
          
        if (studentData && studentData.length > 0) {
          // Create a map of student IDs to emails
          const studentEmailMap = new Map();
          studentData.forEach(student => {
            studentEmailMap.set(student.id, {
              email: student.email,
              first_name: student.first_name,
              last_name: student.last_name
            });
          });
          
          // Update user emails from student data where available
          userWithRoles.forEach(user => {
            if (user.email === 'Email non disponible' && studentEmailMap.has(user.id)) {
              const studentInfo = studentEmailMap.get(user.id);
              user.email = studentInfo.email;
              if (!user.prenom) user.prenom = studentInfo.first_name;
              if (!user.nom) user.nom = studentInfo.last_name;
            }
          });
        }
        
        // Try to find emails in formateurs table
        const { data: formateurData } = await supabase
          .from('formateurs')
          .select('id, email, nom, prenom')
          .in('id', userIds);
          
        if (formateurData && formateurData.length > 0) {
          // Create a map of formateur IDs to emails
          const formateurEmailMap = new Map();
          formateurData.forEach(formateur => {
            formateurEmailMap.set(formateur.id, {
              email: formateur.email,
              nom: formateur.nom,
              prenom: formateur.prenom
            });
          });
          
          // Update user emails from formateur data where available
          userWithRoles.forEach(user => {
            if (user.email === 'Email non disponible' && formateurEmailMap.has(user.id)) {
              const formateurInfo = formateurEmailMap.get(user.id);
              user.email = formateurInfo.email;
              if (!user.prenom) user.prenom = formateurInfo.prenom;
              if (!user.nom) user.nom = formateurInfo.nom;
            }
          });
        }
      }

      setUsers(userWithRoles);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, roleId: string | null, newRole: string) => {
    try {
      setIsLoading(true);
      
      // Trouver l'utilisateur dans notre liste
      const userToUpdate = users.find(u => u.id === userId);
      if (!userToUpdate) {
        throw new Error("Utilisateur non trouvé");
      }
      
      if (roleId) {
        // Update existing role
        const { error } = await supabase
          .from('user_roles')
          .update({ 
            role: newRole,
            // Préserver les données existantes
            nom: userToUpdate.nom,
            prenom: userToUpdate.prenom,
            address_mail: userToUpdate.email !== 'Email non disponible' ? userToUpdate.email : null
          })
          .eq('id', roleId);
        
        if (error) throw error;
      } else {
        // Create new role
        const { error } = await supabase
          .from('user_roles')
          .insert([{ 
            user_id: userId, 
            role: newRole,
            nom: userToUpdate.nom,
            prenom: userToUpdate.prenom,
            address_mail: userToUpdate.email !== 'Email non disponible' ? userToUpdate.email : null
          }]);
        
        if (error) throw error;
      }
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, role: newRole } 
          : user
      ));
      
      // Refresh data to ensure consistency
      await fetchUsers();
      
      setSuccessMessage("Rôle mis à jour avec succès");
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error('Error updating role:', error);
      setError('Erreur lors de la mise à jour du rôle');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = (userToEdit: UserWithRole) => {
    setEditUser(userToEdit);
    setNom(userToEdit.nom || '');
    setPrenom(userToEdit.prenom || '');
    setEmail(userToEdit.email === 'Email non disponible' ? '' : userToEdit.email || '');
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    try {
      setIsLoading(true);
      
      // Update the user_roles table with the new information
      const { error } = await supabase
        .from('user_roles')
        .update({ 
          nom: nom || null,
          prenom: prenom || null,
          address_mail: email || null
        })
        .eq('id', editUser.role_id);
      
      if (error) throw error;
      
      // Update the local state
      setUsers(users.map(u => 
        u.id === editUser.id 
          ? { 
              ...u, 
              nom: nom || null,
              prenom: prenom || null,
              email: email || 'Email non disponible',
              address_mail: email || null
            } 
          : u
      ));
      
      // Reset the edit state
      setEditUser(null);
      setNom('');
      setPrenom('');
      setEmail('');
      
      // Show success message
      setSuccessMessage("Utilisateur mis à jour avec succès");
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Refresh data to ensure consistency
      await fetchUsers();
      
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Erreur lors de la mise à jour de l\'utilisateur');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditUser(null);
    setNom('');
    setPrenom('');
    setEmail('');
  };

  const handleAddUser = async (e: FormEvent) => {
    e.preventDefault();

    // Validation
    if (!email || !password || !nom || !prenom || !selectedRole) {
      setError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Veuillez entrer une adresse email valide");
      return;
    }

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    try {
      setIsLoading(true);
      setIsCreatingUser(true);
      setError(null);

      // Appel de l'edge function admin-create-user. Celle-ci utilise l'Admin API
      // côté serveur → l'admin actuel ne perd plus sa session.
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'admin-create-user',
        {
          body: {
            email,
            password,
            role: selectedRole,
            nom,
            prenom,
          },
        }
      );

      if (fnError) throw fnError;
      if (fnData?.error) throw new Error(fnData.error);

      // Reset form + rafraîchir la liste sans déconnexion.
      setShowModal(false);
      setEmail('');
      setPassword('');
      setNom('');
      setPrenom('');
      setSelectedRole('etudiant');
      setSuccessMessage(`L'utilisateur ${prenom} ${nom} (${email}) a été créé avec succès.`);
      setTimeout(() => setSuccessMessage(null), 4000);
      await fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      const msg = error instanceof Error ? error.message : String(error);
      setError(`Erreur lors de l'ajout de l'utilisateur : ${msg}`);
    } finally {
      setIsLoading(false);
      setIsCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action supprimera également toutes les données associées.')) {
      try {
        setIsLoading(true);
        
        // Instead of directly deleting the user with admin API,
        // we'll delete the user's role from our user_roles table
        const { error } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', userId);
        
        if (error) throw error;
        
        // Update local state
        setUsers(users.filter(user => user.id !== userId));
        
        // Show success message
        setSuccessMessage("Utilisateur supprimé avec succès");
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error('Error deleting user:', error);
        setError('Erreur lors de la suppression de l\'utilisateur');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="animate-fade-in">
      <header className="flex justify-between items-center mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
            <ChevronLeft className="h-4 w-4 mr-1" />
            <span>Retour au tableau de bord</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des utilisateurs</h1>
          <p className="mt-2 text-gray-600">
            Gérez les utilisateurs et leurs rôles
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary inline-flex items-center"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Ajouter un utilisateur
        </button>
      </header>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="bg-success-50 text-success-700 p-4 rounded-md mb-6 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Date de création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  <div className="flex items-center justify-center space-x-2">
                    <AlarmClock className="h-5 w-5 animate-pulse text-gray-400" />
                    <span>Chargement...</span>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  Aucun utilisateur trouvé
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  {editUser?.id === user.id ? (
                    <>
                      <td>
                        <input
                          type="text"
                          value={nom}
                          onChange={(e) => setNom(e.target.value)}
                          className="input-field py-1 px-2"
                          placeholder="Nom"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={prenom}
                          onChange={(e) => setPrenom(e.target.value)}
                          className="input-field py-1 px-2"
                          placeholder="Prénom"
                        />
                      </td>
                      <td>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="input-field py-1 px-2"
                          placeholder="Email"
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{user.nom || "-"}</td>
                      <td>{user.prenom || "-"}</td>
                      <td>{user.email || "Email non disponible"}</td>
                    </>
                  )}
                  <td>
                    <select
                      value={user.role || 'etudiant'}
                      onChange={(e) => handleRoleChange(user.id, user.role_id, e.target.value)}
                      className="input-field py-1 px-2 text-sm"
                      disabled={editUser?.id === user.id}
                    >
                      <option value="admin">Admin</option>
                      <option value="formateur">Formateur</option>
                      <option value="etudiant">Étudiant</option>
                    </select>
                  </td>
                  <td>
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      {editUser?.id === user.id ? (
                        <>
                          <button
                            onClick={handleUpdateUser}
                            className="p-1 text-success-500 hover:text-success-700 rounded-md"
                            title="Enregistrer"
                          >
                            <Save className="h-5 w-5" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-gray-500 hover:text-gray-700 rounded-md"
                            title="Annuler"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-1 text-primary-600 hover:text-primary-800 rounded-md"
                            title="Modifier"
                          >
                            <Edit2 className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-1 text-error-600 hover:text-error-800 rounded-md"
                            title="Supprimer"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4 animate-slide-up">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Ajouter un utilisateur</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-500"
                disabled={isCreatingUser}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    className="input-field"
                    placeholder="Prénom"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="nom"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    className="input-field"
                    placeholder="Nom"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-error-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="email@exemple.com"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe <span className="text-error-500">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder="Mot de passe (min 6 caractères)"
                  required
                  minLength={6}
                />
              </div>
              
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle <span className="text-error-500">*</span>
                </label>
                <select
                  id="role"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="">Sélectionner un rôle</option>
                  <option value="admin">Admin</option>
                  <option value="formateur">Formateur</option>
                  <option value="etudiant">Étudiant</option>
                </select>
              </div>
              
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="text-sm text-amber-800 font-medium mb-1">Attention</div>
                <p className="text-xs text-amber-700">
                  La création d'un nouvel utilisateur vous déconnectera automatiquement. 
                  Vous devrez vous reconnecter manuellement avec votre compte administrateur.
                </p>
              </div>
              
              <div className="flex justify-end mt-6 space-x-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-outline"
                  disabled={isCreatingUser}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isCreatingUser}
                >
                  {isCreatingUser ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Création...
                    </span>
                  ) : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;